/**
 * Default storefront layout — mirrors the hardcoded section order that ShopClient
 * rendered before the section-builder refactor, so a store with NO `storefront`
 * config renders byte-identical to before.
 *
 * `topOfferBanner` and `dealOfTheDay` are available section types but were not
 * rendered by the legacy ShopClient, so they default to `enabled: false`.
 */
import type { SectionEntry, StorefrontConfig } from "@/src/types/storefront";
import { STOREFRONT_SCHEMA_VERSION } from "@/src/types/storefront";

export const DEFAULT_SECTIONS: SectionEntry[] = [
  { id: "hero", type: "hero", enabled: true, order: 0, config: {} },
  { id: "trustStrip", type: "trustStrip", enabled: true, order: 1, config: {} },
  { id: "activeOffers", type: "activeOffers", enabled: true, order: 2, config: {} },
  { id: "bestsellers", type: "bestsellers", enabled: true, order: 3, config: {} },
  { id: "productGrid", type: "productGrid", enabled: true, order: 4, config: {} },
  { id: "shopTheEdit", type: "shopTheEdit", enabled: true, order: 5, config: {} },
  { id: "bundleAndSave", type: "bundleAndSave", enabled: true, order: 6, config: {} },
  { id: "reviews", type: "reviews", enabled: true, order: 7, config: {} },
  { id: "aboutMaker", type: "aboutMaker", enabled: true, order: 8, config: {} },
  // NOTE: "moreNeighbors" is intentionally NOT a configurable section — ShopClient
  // renders it directly above the footer so sellers can't reorder/hide/remove it.
  // Available but not rendered by default (hidden).
  { id: "topOfferBanner", type: "topOfferBanner", enabled: false, order: 10, config: {} },
  { id: "dealOfTheDay", type: "dealOfTheDay", enabled: false, order: 11, config: {} },
];

export const DEFAULT_STOREFRONT: StorefrontConfig = {
  version: STOREFRONT_SCHEMA_VERSION,
  sections: DEFAULT_SECTIONS,
};

/**
 * Resolve the ordered, visible section list to render.
 * Falls back to DEFAULT_SECTIONS when no storefront config is present.
 */
export function resolveRenderSections(storefront?: StorefrontConfig | null): SectionEntry[] {
  const sections = storefront?.sections?.length ? storefront.sections : DEFAULT_SECTIONS;
  return sections
    .filter((s) => s.enabled)
    .slice()
    .sort((a, b) => a.order - b.order);
}
